import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Mail, 
  Edit, 
  Eye,
  Search,
  Save,
  X,
  Loader2,
  Code,
  FileText,
  RefreshCw,
  Copy,
  Check,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Email categories for organization
const EMAIL_CATEGORIES = {
  transactional: { label: 'Transactional', color: 'bg-blue-100 text-blue-800' },
  webinar_lifecycle: { label: 'Webinar Lifecycle', color: 'bg-green-100 text-green-800' },
  behavior_based: { label: 'Behavior Based', color: 'bg-yellow-100 text-yellow-800' },
  re_engagement: { label: 'Re-engagement', color: 'bg-purple-100 text-purple-800' },
  milestones: { label: 'Milestones', color: 'bg-pink-100 text-pink-800' },
  marketing: { label: 'Marketing', color: 'bg-orange-100 text-orange-800' },
  lead_nurturing: { label: 'Lead Nurturing', color: 'bg-cyan-100 text-cyan-800' },
  corporate: { label: 'Corporate', color: 'bg-indigo-100 text-indigo-800' },
  admin: { label: 'Admin', color: 'bg-slate-100 text-slate-800' },
};

// Template Preview Component
const TemplatePreview = ({ subject, htmlContent, variables }) => {
  // Replace variables with sample values for preview
  const sampleValues = {
    first_name: 'John',
    last_name: 'Doe',
    email: 'john.doe@example.com',
    webinar_title: 'Understanding SOC 2 Compliance',
    webinar_date: 'January 15, 2026',
    webinar_time: '2:00 PM EST',
    order_number: 'GRC-2026-001234',
    amount: '$199.00',
    cpe_credits: '2.0',
    speaker_name: 'Dr. Jane Smith',
    frontend_url: window.location.origin,
    company_name: 'Acme Corp',
  };

  let previewHtml = htmlContent || '';
  let previewSubject = subject || '';
  
  Object.entries(sampleValues).forEach(([key, value]) => {
    const regex = new RegExp(`{{${key}}}`, 'g');
    previewHtml = previewHtml.replace(regex, value);
    previewSubject = previewSubject.replace(regex, value);
  });

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Email Header */}
      <div className="bg-slate-100 p-4 border-b">
        <p className="text-sm text-slate-500">Subject:</p>
        <p className="font-semibold">{previewSubject}</p>
      </div>
      {/* Email Body */}
      <div 
        className="p-4 bg-white max-h-[500px] overflow-y-auto"
        dangerouslySetInnerHTML={{ __html: previewHtml }}
      />
    </div>
  );
};

// Simple Text Editor
const SimpleEditor = ({ value, onChange, placeholder }) => (
  <Textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="min-h-[200px] font-mono text-sm"
  />
);

// HTML Code Editor
const HtmlEditor = ({ value, onChange }) => (
  <div className="relative">
    <Textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="min-h-[400px] font-mono text-sm bg-slate-900 text-green-400"
      placeholder="Enter HTML content..."
    />
    <div className="absolute top-2 right-2">
      <span className="text-xs bg-slate-700 text-slate-300 px-2 py-1 rounded">HTML</span>
    </div>
  </div>
);

export default function AdminEmailTemplatesPage() {
  const { user, isAuthenticated } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Get token from localStorage since AuthContext doesn't expose it
  const token = localStorage.getItem('grc_token');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editorMode, setEditorMode] = useState('simple'); // 'simple' or 'html'
  const [saving, setSaving] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    subject: '',
    preview_text: '',
    html_content: '',
  });

  useEffect(() => {
    if (token) {
      fetchTemplates();
    }
  }, [token]);

  const fetchTemplates = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load email templates');
    }
    setLoading(false);
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setEditForm({
      subject: template.subject || '',
      preview_text: template.preview || '',
      html_content: template.content || '',
    });
    setEditModalOpen(true);
  };

  const handlePreview = (template) => {
    setSelectedTemplate(template);
    setPreviewModalOpen(true);
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/email-templates/${selectedTemplate.name}`,
        {
          subject: editForm.subject,
          preview: editForm.preview_text,
          content: editForm.html_content,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template saved successfully');
      setEditModalOpen(false);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    }
    setSaving(false);
  };

  const handleResetToDefault = async () => {
    if (!selectedTemplate) return;
    
    try {
      await axios.post(
        `${API_URL}/api/admin/email-templates/${selectedTemplate.name}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template reset to default');
      fetchTemplates();
      setEditModalOpen(false);
    } catch (error) {
      toast.error('Failed to reset template');
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getCategoryForTemplate = (templateName) => {
    const categoryMap = {
      welcome: 'transactional',
      email_verification: 'transactional',
      password_reset: 'transactional',
      password_changed: 'transactional',
      order_confirmation: 'transactional',
      payment_receipt: 'transactional',
      invoice_email: 'transactional',
      payment_failed: 'transactional',
      refund_processed: 'transactional',
      registration_confirmed: 'transactional',
      profile_updated: 'transactional',
      account_deactivated: 'transactional',
      webinar_reminder_7_days: 'webinar_lifecycle',
      webinar_reminder_24_hours: 'webinar_lifecycle',
      webinar_reminder_1_hour: 'webinar_lifecycle',
      webinar_starting_now: 'webinar_lifecycle',
      webinar_missed: 'webinar_lifecycle',
      post_webinar_thank_you: 'webinar_lifecycle',
      recording_available: 'webinar_lifecycle',
      certificate_ready: 'webinar_lifecycle',
      cpe_credits_issued: 'webinar_lifecycle',
      webinar_rescheduled: 'webinar_lifecycle',
      webinar_cancelled: 'webinar_lifecycle',
      abandoned_checkout_1hr: 'behavior_based',
      abandoned_checkout_24hr: 'behavior_based',
      abandoned_checkout_72hr: 'behavior_based',
      browse_abandonment: 'behavior_based',
      wishlist_reminder: 'behavior_based',
      price_drop_alert: 'behavior_based',
      almost_sold_out: 'behavior_based',
      back_in_stock: 'behavior_based',
      we_miss_you_7_days: 're_engagement',
      we_miss_you_30_days: 're_engagement',
      we_miss_you_60_days: 're_engagement',
      we_miss_you_90_days: 're_engagement',
      incomplete_profile: 're_engagement',
      no_purchase_yet: 're_engagement',
      first_webinar_completed: 'milestones',
      milestone_5_webinars: 'milestones',
      milestone_10_webinars: 'milestones',
      cpe_goal_progress: 'milestones',
      annual_summary: 'milestones',
      birthday_email: 'milestones',
      anniversary_email: 'milestones',
      new_webinar_announcement: 'marketing',
      speaker_spotlight: 'marketing',
      industry_news_digest: 'marketing',
      recommended_for_you: 'marketing',
      flash_sale: 'marketing',
      early_bird_access: 'marketing',
      bundle_offer: 'marketing',
      seasonal_campaign: 'marketing',
      referral_program: 'marketing',
      lead_welcome: 'lead_nurturing',
      lead_nurture_day_3: 'lead_nurturing',
      lead_nurture_day_7: 'lead_nurturing',
      lead_nurture_day_14: 'lead_nurturing',
      lead_nurture_day_21: 'lead_nurturing',
      lead_status_changed: 'lead_nurturing',
      lead_assigned: 'lead_nurturing',
      corporate_welcome: 'corporate',
      team_member_invited: 'corporate',
      team_usage_report: 'corporate',
      license_expiring: 'corporate',
      license_renewal: 'corporate',
      new_seats_added: 'corporate',
      admin_new_user: 'admin',
      admin_new_order: 'admin',
      admin_high_value_lead: 'admin',
      admin_support_ticket: 'admin',
      admin_webinar_full: 'admin',
      admin_daily_summary: 'admin',
      admin_weekly_report: 'admin',
    };
    return categoryMap[templateName] || 'other';
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          template.subject?.toLowerCase().includes(searchTerm.toLowerCase());
    const category = getCategoryForTemplate(template.name);
    const matchesCategory = selectedCategory === 'all' || category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-navy-500" />
      </div>
    );
  }

  return (
    <div data-testid="admin-email-templates-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-600">Email Templates</h1>
          <p className="text-slate-500">View, edit, and customize all 72 email templates</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchTemplates} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => window.location.href = '/admin/email-builder'} size="sm">
            <Mail className="w-4 h-4 mr-2" />
            Create New Email
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500"
        >
          <option value="all">All Categories</option>
          {Object.entries(EMAIL_CATEGORIES).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Templates Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-navy-500" />
            Email Templates ({filteredTemplates.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Mail className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No templates found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Template Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Variables</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTemplates.map((template) => {
                    const category = getCategoryForTemplate(template.name);
                    const categoryInfo = EMAIL_CATEGORIES[category] || { label: 'Other', color: 'bg-gray-100 text-gray-800' };
                    
                    return (
                      <TableRow key={template.name}>
                        <TableCell>
                          <span className="font-medium">{template.name?.replace(/_/g, ' ')}</span>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${categoryInfo.color}`}>
                            {categoryInfo.label}
                          </span>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {template.subject}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-500">
                            {template.variables?.length || 0} vars
                          </span>
                        </TableCell>
                        <TableCell>
                          {template.is_customized ? (
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                              Customized
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
                              Default
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePreview(template)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(template)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-500" />
              Preview: {selectedTemplate?.name?.replace(/_/g, ' ')}
            </DialogTitle>
            <DialogDescription>
              Variables are replaced with sample values for preview
            </DialogDescription>
          </DialogHeader>
          
          {selectedTemplate && (
            <TemplatePreview
              subject={selectedTemplate.subject}
              htmlContent={selectedTemplate.content}
              variables={selectedTemplate.variables}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-green-500" />
              Edit: {selectedTemplate?.name?.replace(/_/g, ' ')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Subject */}
            <div className="space-y-2">
              <Label>Subject Line</Label>
              <Input
                value={editForm.subject}
                onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                placeholder="Email subject..."
              />
            </div>

            {/* Preview Text */}
            <div className="space-y-2">
              <Label>Preview Text (appears in inbox)</Label>
              <Input
                value={editForm.preview_text}
                onChange={(e) => setEditForm({ ...editForm, preview_text: e.target.value })}
                placeholder="Preview text..."
              />
            </div>

            {/* Available Variables */}
            {selectedTemplate?.variables?.length > 0 && (
              <div className="space-y-2">
                <Label>Available Variables (click to copy)</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedTemplate.variables.map((variable) => (
                    <button
                      key={variable}
                      onClick={() => copyToClipboard(`{{${variable}}}`, variable)}
                      className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-sm font-mono flex items-center gap-1 transition-colors"
                    >
                      {`{{${variable}}}`}
                      {copiedField === variable ? (
                        <Check className="w-3 h-3 text-green-500" />
                      ) : (
                        <Copy className="w-3 h-3 text-slate-400" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Editor Mode Toggle */}
            <div className="flex items-center gap-2">
              <Button
                variant={editorMode === 'simple' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setEditorMode('simple')}
              >
                <FileText className="w-4 h-4 mr-1" />
                Simple
              </Button>
              <Button
                variant={editorMode === 'html' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setEditorMode('html')}
              >
                <Code className="w-4 h-4 mr-1" />
                HTML
              </Button>
            </div>

            {/* Content Editor */}
            <div className="space-y-2">
              <Label>Email Content</Label>
              {editorMode === 'simple' ? (
                <SimpleEditor
                  value={editForm.html_content}
                  onChange={(val) => setEditForm({ ...editForm, html_content: val })}
                  placeholder="Enter email content..."
                />
              ) : (
                <HtmlEditor
                  value={editForm.html_content}
                  onChange={(val) => setEditForm({ ...editForm, html_content: val })}
                />
              )}
            </div>

            {/* Live Preview */}
            <div className="space-y-2">
              <Label>Live Preview</Label>
              <TemplatePreview
                subject={editForm.subject}
                htmlContent={editForm.html_content}
                variables={selectedTemplate?.variables}
              />
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            <Button variant="outline" onClick={handleResetToDefault}>
              Reset to Default
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
